<template>
  <FormField
    :label="label"
    :labelFor="defaultId"
    :error="error"
    :success="success"
    :showErrorFeedbackMessage="showErrorFeedbackMessage"
    :showSuccessFeedbackMessage="showSuccessFeedbackMessage"
    :feedbackMessage="feedbackMessage"
    :infoIcon="infoIcon"
    :infoText="infoText"
    :tooltipMinWidth="tooltipMinWidth"
    :size="size"
    :class="{ disabled: disabled }"
  >
    <SelectCoupon
      v-bind="$attrs"
      :value="value"
      :options="options"
      :search="search"
      :searchPlaceholder="searchPlaceholder"
      :multiple="multiple"
      :capitalize="capitalize"
      :toggleAll="toggleAll"
      :toggleAllLabel="toggleAllLabel"
      :showAllOptionsSelectedText="showAllOptionsSelectedText"
      :disabled="disabled"
      :loadOnDemand="loadOnDemand"
      :wasUsed="wasUsed"
      @input="eventHandler"
      @change="eventHandler"
      @focus="$emit('focus', $event)"
      @search="$emit('search', $event)"
    >
      <template v-for="(_, name) in $scopedSlots" v-slot:[name]="slotData"
        ><slot :name="name" v-bind="slotData"
      /></template>
    </SelectCoupon>

    <!-- CONTADOR -->
    <PmwParagraph v-if="showCounter" size="paragraph-40" class="select--counter"
      >{{ valueLength }}/{{ $attrs.maxQuantitySelection }}
      {{ counterText }}</PmwParagraph
    >
  </FormField>
</template>

<script>
/** Componente de select para tarifas de cupom de desconto e desconto exclusivo */
import PmwParagraph from '@solucoes/components-lib-tmp/src/components/elements/PmwParagraph'
import { getRandomInt } from '@/helpers/math'
import SelectCoupon from './SelectCoupon'
import FormField from '../../patterns/FormField'

export default {
  name: 'FormSelectCoupon',
  components: {
    FormField,
    SelectCoupon,
    PmwParagraph
  },
  props: {
    /**
     * Opções do select
     * @example [{
          value: '10',
          text: 'Opção'
        },
        {
          label: 'Pmweb Campinas',
          options: [
            { value: { C: '3PO' }, text: 'Early Bird 10%' },
            { value: { R: '2D2' }, text: 'Last Minute' }
          ]
        }]
     */

    options: {
      type: Array,
      default: () => [],
      required: true
    },
    /**
     * Label do campo
     */
    label: {
      type: String,
      default: 'Label do input'
    },
    /**
     * Placeholder do campo
     */
    placeholder: {
      type: String,
      default: 'Insira o conteúdo'
    },
    /**
     * Se possuí busca de opções
     */
    search: {
      type: Boolean,
      default: false
    },
    /**
     * placeholder do campo de busca
     */
    searchPlaceholder: {
      type: String,
      default: 'Buscar'
    },
    /**
     * Tipo do campo
     */
    type: {
      type: String,
      default: 'text'
    },
    /**
     * Se o campo está com erro de validação
     */
    error: {
      type: Boolean,
      default: false
    },
    /**
     * Se o campo passou na validação
     */
    success: {
      type: Boolean,
      default: false
    },
    /**
     * Se deve mostrar uma mensagem de feedback caso ocorra erro
     */
    showErrorFeedbackMessage: {
      type: Boolean,
      default: false
    },
    /**
     * Se deve mostrar uma mensagem de feedback caso ocorra sucesso
     */
    showSuccessFeedbackMessage: {
      type: Boolean,
      default: false
    },
    /**
     * Texto da mensagem de feedback
     */
    feedbackMessage: {
      type: String,
      default: 'Campo obrigatório'
    },
    /**
     * Nome do campo
     */
    name: {
      type: String,
      default: ''
    },
    /**
     * Valor do campo
     */
    value: {
      type: [String, Number, Object, Array],
      default: ''
    },
    /**
     * Se é obrigatório
     */
    required: {
      type: Boolean,
      default: false
    },
    /**
     * Se é apenas leitura
     */
    readonly: {
      type: Boolean,
      default: false
    },
    /**
     * Se deve apresentar um ícone de informação sobre o campo
     */
    infoIcon: {
      type: Boolean,
      default: false
    },
    /**
     * Texto de informação
     */
    infoText: {
      type: String,
      default: 'Informação sobre o campo'
    },
    /**
     * Se o select permite multiplas escolhas
     */
    multiple: {
      type: Boolean,
      default: false
    },
    /** largura mínima da tooltip */
    tooltipMinWidth: {
      type: [Number, String],
      default: 150
    },
    /** Se deve capitalizar os textos das opções e opção selecionada */
    capitalize: {
      type: Boolean,
      default: false
    },
    /**
     * Se deve apresentar uma opção para selecionar todas as opções
     */
    toggleAll: {
      type: Boolean,
      default: true
    },
    /**
     * Texto da opção de seleção de todas
     */
    toggleAllLabel: {
      type: String,
      default: 'Todas'
    },
    /** Se deve apresentar o texto de todas opções selecionadas */
    showAllOptionsSelectedText: {
      type: Boolean,
      default: true
    },
    /**
     * Largura do campo
     * small, large, fluid
     */
    size: {
      type: String,
      default: 'fluid'
    },
    /** Se deve desabilitar o input */
    disabled: {
      type: Boolean,
      default: false
    },
    /** Se deve carregar as opções sob demanda */
    loadOnDemand: {
      type: Boolean,
      default: false
    },
    /** Se o cupom já foi utilizado */
    wasUsed: {
      type: Boolean,
      default: false
    },
    showCounter: Boolean,
    counterText: {
      type: String,
      default: 'tarifas selecionadas'
    },
    hotelLabelKey: {
      type: String,
      default: 'nome'
    },
    rateLabelKey: {
      type: String,
      default: 'descricao'
    },
    rateValueKey: {
      type: String,
      default: 'codigo'
    }
  },
  data() {
    return {
      defaultId: `form-select-${getRandomInt(0, 99999)}`
    }
  },

  computed: {
    valueLength() {
      return this.value?.length ?? 0
    }
  },

  methods: {
    eventHandler($event) {
      this.$emit('input', $event)
      this.$emit('change', $event)
    }
  }
}
</script>

<style lang="scss" scoped>
.select {
  position: relative;

  &--counter {
    position: absolute;
    right: 0;
    top: calc(100% + 3px);
    color: pmw-colors('grey-70');
  }
}

::v-deep {
  .select {
    &--button {
      &:focus {
        border-color: $primary;
        border-width: 2px;
      }
    }
  }
}
</style>
